import { useEffect, useRef } from "react";

import { SelectItem } from "@fronterahealth/frontera-ui-components";

import { ApiReportGoalChartTypeChoices } from "@api/graphql/types-and-hooks";
import { useAnalytics } from "@providers/AnalyticsProvider";

// Built once at module scope so the effect's identity-based deps stay stable.
const CUMULATIVE_SELECT_ITEM: SelectItem = {
  id: ApiReportGoalChartTypeChoices.Cumulative,
  primary: "Cumulative mastered targets",
};

interface RestrictToCumulativeArgs {
  goalId: string;
  hasMixedMeasurementTypes: boolean;
  axisGroupList: string[];
  selectedChart: SelectItem;
  setSelectedChart: (item: SelectItem) => void;
}

/**
 * When a goal's targets span multiple Y-axis groups, dispatch the auto-switch to
 * Cumulative and fire the PostHog event. Deduped per (goalId, axisGroupKey) so it
 * runs once per goal-and-axis-set combination, even when the parent passes a no-op
 * `setSelectedChart` (review pages).
 */
export const useRestrictToCumulative = ({
  goalId,
  hasMixedMeasurementTypes,
  axisGroupList,
  selectedChart,
  setSelectedChart,
}: RestrictToCumulativeArgs): void => {
  const analytics = useAnalytics();
  const lastRestrictedKeyRef = useRef<string | null>(null);

  const restrictionKey = hasMixedMeasurementTypes ? `${goalId}|${axisGroupList.join(",")}` : null;

  useEffect(() => {
    if (!restrictionKey) {
      lastRestrictedKeyRef.current = null;
      return;
    }
    if (lastRestrictedKeyRef.current === restrictionKey) return;
    lastRestrictedKeyRef.current = restrictionKey;
    analytics?.capture("goal_chart_restricted_to_cumulative", {
      goal_id: goalId,
      previous_chart_type: selectedChart.id,
      axis_groups: axisGroupList,
    });
    if (selectedChart.id !== ApiReportGoalChartTypeChoices.Cumulative) {
      setSelectedChart(CUMULATIVE_SELECT_ITEM);
    }
  }, [restrictionKey, goalId, selectedChart.id, setSelectedChart, analytics]);
};
